export interface Volume {
  number: string
  title: string
  subtitle: string
  bullets: string[]
  result: string
  featured?: boolean
}

export const volumes: Volume[] = [
  {
    number: 'Volume 1',
    title: 'Drawing Foundations & Visual Language',
    subtitle: 'The foundational base that holds everything together.',
    bullets: [
      'Line control and line quality',
      'Shape recognition and contour drawing',
      'Form and basic construction',
      'Observation and accuracy',
      'Negative space and visual relationships',
      'Foundational correction strategies',
      'Beginner assessment tools',
    ],
    result:
      'Your beginners feel supported, capable, and motivated to come back from their first class.',
  },
  {
    number: 'Volume 2',
    title: 'Observation, Proportion & Construction',
    subtitle:
      "Deepen the student's ability to observe, measure, and construct with precision.",
    bullets: [
      'Observation systems',
      'Comparative measurement',
      'Proportion and landmark placement',
      'Alignment and shape relationships',
      'Volume and structural thinking',
      'Correcting proportion errors',
      'Building before rendering',
    ],
    result:
      'Students stop guessing and start using visible relationships and verifiable structures.',
  },
  {
    number: 'Volume 3',
    title: 'Perspective, Space, Light & Composition',
    subtitle: 'Teach how to build depth, lighting, and visual organization.',
    bullets: [
      'Perspective and spatial relationships',
      'Horizon, vanishing points, convergence',
      'Light direction and value grouping',
      'Form shadow and cast shadow',
      'Focal points and visual hierarchy',
      'Composition planning and thumbnails',
      'Edge behavior and light logic',
    ],
    result:
      'Your students build believable space and control viewer attention with confidence.',
  },
  {
    number: 'Volume 4',
    title: 'Figure, Portrait, Anatomy & Advanced Rendering',
    subtitle: 'One of the most advanced volumes in the system.',
    bullets: [
      'Gesture and line of action',
      'Human proportion and body relationships',
      'Functional anatomy for drawing',
      'Head construction and facial proportions',
      'Hand and foot construction',
      'Foreshortening and advanced rendering',
      'Materials, edges, and texture',
    ],
    result:
      'Your students develop the foundation to draw figures and portraits with accuracy and confidence.',
  },
  {
    number: 'Volume 5',
    title: 'Complete Structured Progression',
    subtitle: 'The organized framework connecting all drawing skills.',
    bullets: [
      'Skill sequencing from foundation to advanced',
      'Cross-skill integration opportunities',
      'Advanced observational drawing',
      'Complex construction and rendering',
      'Mixed-skill integration projects',
      'Final integrated drawing projects',
    ],
    result:
      'Your entire program flows logically, and students see how each skill connects to the next.',
  },
  {
    number: 'Volume 6',
    title: '150 Complete Ready-to-Teach Lessons',
    subtitle: 'The operational heart of the system.',
    bullets: [
      '150 structured lessons for all levels',
      'Lessons organized by skill and progression',
      'Complete class structures with timing',
      'Teacher cues and common error guides',
      'Assessment points built in',
      'Optional extensions included',
      'Nothing to construct from zero',
    ],
    result:
      'Open, choose a lesson, and teach. Your preparation time drops from hours to minutes.',
    featured: true,
  },
]

export interface LessonGroup {
  range: string
  title: string
  detail: string
}

export const lessonGroups: LessonGroup[] = [
  {
    range: 'Lessons 1–6',
    title: 'Line & Control',
    detail: 'Line confidence, quality, control, repetition, pressure, direction.',
  },
  {
    range: 'Lessons 7–18',
    title: 'Observation & Shape',
    detail: 'Observation, contour, silhouette, shape recognition, negative space.',
  },
  {
    range: 'Lessons 19–30',
    title: 'Form & Proportion',
    detail:
      'Proportion, measurement, landmarks, major forms, construction, volume.',
  },
  {
    range: 'Lessons 31–45',
    title: 'Perspective',
    detail:
      'Horizon, vanishing directions, boxes, planes, spatial relationships, depth.',
  },
  {
    range: 'Lessons 46–60',
    title: 'Value & Light',
    detail:
      'Value groups, light direction, form shadows, cast shadows, edge transitions.',
  },
  {
    range: 'Lessons 61–75',
    title: 'Composition',
    detail:
      'Thumbnails, focal areas, cropping, hierarchy, balance, visual organization.',
  },
  {
    range: 'Lessons 76–90',
    title: 'Figure & Gesture',
    detail:
      'Gesture, line of action, body rhythm, weight, balance, human proportion.',
  },
  {
    range: 'Lessons 91–105',
    title: 'Portrait & Anatomy',
    detail:
      'Head structure, facial proportions, feature placement, anatomy, landmarks.',
  },
  {
    range: 'Lessons 106–120',
    title: 'Hands, Feet & Rendering',
    detail:
      'Hand and foot construction, edges, material rendering, value transitions.',
  },
  {
    range: 'Lessons 121–150',
    title: 'Advanced Integration',
    detail:
      'Complex observational drawing, figure, portrait, perspective, advanced projects.',
  },
]
